import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Target, Terminal, Server, Box, Database, Layers, Monitor, HardDrive, Network, Shield, Sparkles } from 'lucide-react';

const LinuxAdministration = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Linux-Administration Mönchengladbach',
    description: 'Linux-Administration Mönchengladbach: Ubuntu Server, Docker, Proxmox, VMware, Hyper-V, MariaDB, Supabase, MS-SQL. Professionelle Betreuung für KMU.',
    provider: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' },
    areaServed: ['Mönchengladbach', 'Viersen', 'Krefeld', 'Neuss', 'Düsseldorf', 'Nordrhein-Westfalen'],
    serviceType: 'Linux-Administration'
  };

  const leistungen = [
    { icon: Terminal, text: 'Ubuntu Server – Installation, Konfiguration und Härtung' },
    { icon: Box, text: 'Docker – Container-Umgebungen aufbauen und betreiben' },
    { icon: Layers, text: 'Proxmox VE – Virtuelle Maschinen und Container auf einer Plattform' },
    { icon: Monitor, text: 'VMware vSphere – Verwaltung und Migration bestehender Umgebungen' },
    { icon: Server, text: 'Hyper-V – Microsofts Virtualisierung für hybride Infrastrukturen' },
    { icon: Network, text: 'Netzwerkdienste – DNS, DHCP, Reverse Proxy (Nginx), VPN' },
    { icon: HardDrive, text: 'Backup-Konzepte für Linux- und virtualisierte Umgebungen' }
  ];

  const datenbanken = [
    { icon: Database, name: 'MariaDB / MySQL', text: 'Installation, Optimierung, Backup und Replikation. Der Open-Source-Standard für Webanwendungen und Geschäftssoftware.' },
    { icon: Database, name: 'Supabase', text: 'Die moderne Open-Source-Alternative zu Firebase. PostgreSQL-basiert mit Auth, Echtzeit-Funktionen und API.' },
    { icon: Database, name: 'Microsoft SQL Server', text: 'Administration, Performance-Tuning und Integration mit Ihren Windows-Anwendungen und Geschäftsprozessen.' }
  ];

  const szenarien = [
    {
      branche: 'Agentur / Softwareentwicklung',
      text: 'Proxmox-Server mit Docker-Containern. Jedes Kundenprojekt isoliert. Deployment per Skript. Automatische Backups auf separatem Storage.'
    },
    {
      branche: 'Handwerksbetrieb',
      text: 'Hyper-V-Server für ERP und Dateiablage. Getrennte virtuelle Maschinen für Buchhaltung und Werkstatt. MariaDB für die Auftragsverwaltung.'
    },
    {
      branche: 'Arztpraxis / Gesundheitswesen',
      text: 'Ubuntu-Server als verschlüsseltes Backup-Ziel. Automatische Datensicherung nachts. Strikte Zugriffsrechte nach DSGVO.'
    },
    {
      branche: 'Einzelhandel / E-Commerce',
      text: 'Docker-Container für den Webshop. MariaDB als Produktdatenbank. Supabase für Kundenbewertungen und Echtzeit-Bestandsdaten.'
    }
  ];

  const vorteile = [
    'Persönlicher Ansprechpartner – kein wechselndes Support-Team',
    'Vor-Ort-Service im Raum Mönchengladbach, Viersen, Krefeld (20 km Radius)',
    'Erfahrung mit gemischten Umgebungen (Windows + Linux)',
    'Pragmatische Lösungen statt Over-Engineering',
    'Transparente Kommunikation ohne Fachjargon',
    'Flexible Betreuungsmodelle – vom Einzelprojekt bis zur laufenden Wartung'
  ];

  const relatedPages = [
    { title: 'IT-Administrator Mönchengladbach', url: '/it-administrator-moenchengladbach', description: 'Umfassende IT-Administration für Ihr Unternehmen' },
    { title: 'Virtualisierung & Linux', url: '/virtualisierung-linux', description: 'Detaillierte Übersicht aller Virtualisierungs-Leistungen' },
    { title: 'IT-Infrastruktur für KMU', url: '/it-infrastruktur-kmu', description: 'Server, Netzwerk und Backup – die Basis Ihrer IT' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Linux-Administration & Virtualisierung Mönchengladbach – Schteffann"
        description="Linux-Administration Mönchengladbach: Ubuntu Server, Docker, Proxmox, VMware, Hyper-V, MariaDB, Supabase, MS-SQL. Professionelle Betreuung für KMU in Mönchengladbach, Viersen, Krefeld."
        keywords="Linux Administration Mönchengladbach, Ubuntu Server, Docker Mönchengladbach, Proxmox, VMware, Hyper-V, Virtualisierung KMU, MariaDB, Supabase, MS-SQL, Containerisierung, Linux IT-Dienstleistungen, Viersen, Krefeld, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400/15 to-blue-500/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              Linux & Virtualisierung
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Linux-Administration in </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Mönchengladbach</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Ubuntu Server, Docker, Proxmox, VMware, Hyper-V und Datenbanken – professionell betreut für kleine und mittelständische Unternehmen. Persönlich, pragmatisch, ohne Overhead.
            </p>
          </div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Terminal className="w-4 h-4 mr-2" />
              Leistungen
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Linux, Virtualisierung & Container für KMU
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Nicht jedes Unternehmen braucht eine reine Microsoft-Welt. Viele Tools und Dienste laufen besser, günstiger oder flexibler auf Linux – ich helfe Ihnen dabei.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {leistungen.map((item, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all flex-shrink-0">
                      <item.icon className="h-6 w-6 text-cyan-600" />
                    </div>
                    <p className="text-slate-700 leading-relaxed pt-2">{item.text}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Datenbanken */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Datenbank-Administration
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Von klassisch bis modern – ich betreue Ihre Datenbanken zuverlässig und sorge für Sicherheit, Performance und Backup.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {datenbanken.map((item, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200 bg-white">
                  <CardContent className="p-8">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all">
                        <item.icon className="h-6 w-6 text-cyan-600" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900">{item.name}</h3>
                    </div>
                    <p className="text-slate-600 leading-relaxed">{item.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Praxis-Szenarien */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Praxis-Beispiele: So setzen KMU Linux und Virtualisierung ein
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {szenarien.map((szenario, index) => (
                <Card key={index} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-8">
                    <Badge className="mb-4 bg-cyan-100 text-cyan-800 border-cyan-200">
                      {szenario.branche}
                    </Badge>
                    <p className="text-slate-600 leading-relaxed">{szenario.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Vorteile */}
          <Card className="bg-gradient-to-br from-slate-50 to-cyan-50/30 border-0 shadow-xl mb-20">
            <CardContent className="p-12 lg:p-16">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  Warum KMU in Mönchengladbach mit mir arbeiten
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {vorteile.map((vorteil, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                    <p className="text-lg text-slate-700">{vorteil}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Related */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <Badge className="mb-6 bg-cyan-100 text-cyan-800 border-cyan-200 px-6 py-2">
                <Target className="w-4 h-4 mr-2" />
                Weitere Leistungen
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Das könnte Sie auch interessieren
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {relatedPages.map((page, index) => (
                <Link key={index} to={page.url}>
                  <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                    <CardContent className="p-8">
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-3">
                        {page.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{page.description}</p>
                      <div className="flex items-center text-cyan-600 group-hover:text-cyan-700">
                        <span className="text-sm font-medium">Mehr erfahren</span>
                        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Linux-Projekt geplant? Server-Virtualisierung nötig?
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Ich berate Sie ehrlich und unverbindlich – und sage Ihnen, was für Ihr Unternehmen wirklich Sinn macht.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Kontakt aufnehmen
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default LinuxAdministration;
